/**
 * Accelerator Oscillator (AC)
 *
 * Bill Williams' signal for trend acceleration/deceleration.
 * AC = AO − SMA(AO, 5), where AO = SMA(HL2, 5) − SMA(HL2, 34).
 *
 * Category: Advanced | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA } from "./sma";

export const acceleratorIndicator: IndicatorDefinition = {
  id: "accelerator",
  name: "Accelerator Oscillator",
  shortName: "AC",
  category: "advanced",
  pane: "separate",
  params: [
    { key: "fast", label: "Fast Period", type: "number", default: 5, min: 2, max: 50 },
    { key: "slow", label: "Slow Period", type: "number", default: 34, min: 5, max: 200 },
    { key: "smooth", label: "AC Smoothing", type: "number", default: 5, min: 2, max: 50 },
  ],
  plots: [
    { key: "ac", label: "AC", color: "#4CAF50", style: "histogram", lineWidth: 1, priceScaleId: "ac", histogramBase: 0 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { fast, slow, smooth } = params;
    const hl2 = candles.map(c => (c.high + c.low) / 2);
    const smaFast = computeSMA(hl2, fast);
    const smaSlow = computeSMA(hl2, slow);

    const ao: number[] = [];
    for (let i = 0; i < candles.length; i++) {
      if (!isNaN(smaFast[i]) && !isNaN(smaSlow[i])) {
        ao.push(smaFast[i] - smaSlow[i]);
      } else {
        ao.push(NaN);
      }
    }

    const aoValid = ao.filter(v => !isNaN(v));
    const aoSma = computeSMA(aoValid, smooth);

    const data: { time: number; value: number; color?: string }[] = [];
    let validIdx = 0;
    for (let i = 0; i < ao.length; i++) {
      if (!isNaN(ao[i])) {
        if (validIdx < aoSma.length && !isNaN(aoSma[validIdx])) {
          const val = ao[i] - aoSma[validIdx];
          const prevVal = data.length > 0 ? data[data.length - 1].value : 0;
          data.push({
            time: candles[i].time,
            value: val,
            color: val >= prevVal ? "#4CAF50" : "#F44336",
          });
        }
        validIdx++;
      }
    }

    return [{ key: "ac", data }];
  },
};
